import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  BellRing,
  CalendarDays,
  CalendarPlus,
  ChevronDown,
  ExternalLink,
  MapPin,
  Ticket,
  Zap,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Countdown } from "@/components/Countdown";
import { ReminderModal } from "@/components/ReminderModal";
import { fetchEventBySlug } from "@/lib/api";
import { fmtDate, fmtRange } from "@/lib/format";
import { googleCalendarUrl, downloadIcs } from "@/lib/calendar";

const CAT = {
  festival: { label: "Festival", color: "#00E676" },
  concert: { label: "Concert", color: "#A855F7" },
  sport: { label: "Sport & Fit", color: "#FF6B00" },
  club: { label: "Rave & Indoor", color: "#00E5FF" },
  voetbal: { label: "Voetbal", color: "#FFC53D" },
};

const BASE_TITLE = "WanneerKaartverkoop.nl — Nooit meer te laat voor je kaartjes";

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

export default function EventDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchEventBySlug(slug)
      .then(setEvent)
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!event) return;
    const url = `https://wanneerkaartverkoop.nl/event/${event.slug}`;
    const desc = event.sale_start
      ? `De kaartverkoop van ${event.name} (${event.venue}, ${event.city}) start op ${fmtDate(event.sale_start, true)}. Zet een alert en wees er op tijd bij.`
      : `De kaartverkoopdatum van ${event.name} (${event.venue}, ${event.city}) is nog niet bekend. Zet een alert en hoor het als eerste.`;
    document.title = `Wanneer start kaartverkoop ${event.name}? | Alle data & pre-sale info`;
    setMeta("name", "description", desc);
    setMeta("property", "og:title", document.title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    if (event.image_url) setMeta("property", "og:image", event.image_url);
    let link = document.head.querySelector('link[rel="canonical"]');
    if (link) link.setAttribute("href", url);
    const ld = {
      "@context": "https://schema.org",
      "@type": "Event",
      name: event.name,
      url,
      ...(event.image_url ? { image: [event.image_url] } : {}),
      ...(event.event_date ? { startDate: event.event_date } : {}),
      ...(event.event_end ? { endDate: event.event_end } : {}),
      eventStatus: "https://schema.org/EventScheduled",
      location: {
        "@type": "Place",
        name: event.venue,
        address: { "@type": "PostalAddress", addressLocality: event.city, addressCountry: "NL" },
      },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "event-jsonld";
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => {
      document.title = BASE_TITLE;
      document.getElementById("event-jsonld")?.remove();
    };
  }, [event]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col bg-obsidian text-white">
        <Header onOrganizer={() => nav("/contact")} />
        <main className="flex flex-1 flex-col items-center justify-center gap-4 px-4 pt-16 text-center">
          <h1 className="font-display text-2xl font-bold">Event niet gevonden</h1>
          <Link to="/" className="text-sm font-semibold text-volt hover:underline" data-testid="event-not-found-home-link">
            Terug naar het overzicht
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-obsidian pt-16 text-white">
        <Header onOrganizer={() => nav("/contact")} />
        <div className="mx-auto max-w-5xl px-4 py-24">
          <div className="h-64 animate-pulse rounded-3xl bg-panel" />
        </div>
      </div>
    );
  }

  const badge = CAT[event.category] || CAT.festival;
  const badgeLabel = event.genre || badge.label;
  const isLive = event.status === "live";
  const isExpected = event.status === "verwacht";
  const gUrl = googleCalendarUrl(event);

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="event-detail-page">
      <Header onOrganizer={() => nav("/contact")} />

      <main className="pt-16">
        <div className="relative h-[38vh] min-h-64 overflow-hidden">
          {event.image_url && (
            <motion.img
              initial={{ scale: 1.08 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              src={event.image_url}
              alt={event.name}
              className="h-full w-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#070709] via-[#070709]/50 to-[#070709]/30" />
          <Link
            to="/"
            data-testid="event-detail-back-link"
            className="absolute left-4 top-6 inline-flex items-center gap-1.5 rounded-full bg-black/60 px-4 py-2 text-xs font-semibold text-slate-200 backdrop-blur transition-colors hover:text-volt sm:left-6"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Alle events
          </Link>
          {isLive && (
            <span className="absolute right-4 top-6 inline-flex items-center gap-1.5 rounded-full bg-[#FF3B30] px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-white sm:right-6" data-testid="event-detail-live-badge">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
              </span>
              Nu live
            </span>
          )}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative pb-24">
          <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr]">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest backdrop-blur" style={{ color: badge.color }}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: badge.color }} />
                {badgeLabel}
              </span>
              <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
                {event.name}
              </h1>
              <div className="mt-5 space-y-2 text-base text-slate-300">
                <p className="flex items-center gap-2.5">
                  <MapPin className="h-4 w-4 shrink-0 text-slate-500" />
                  {event.venue} · {event.city}
                </p>
                {event.event_date && (
                  <p className="flex items-center gap-2.5">
                    <CalendarDays className="h-4 w-4 shrink-0 text-slate-500" />
                    {fmtRange(event.event_date, event.event_end)}
                  </p>
                )}
              </div>
              {event.status_note && (
                <p className="mt-5 inline-block rounded-xl border border-blaze/30 bg-blaze/10 px-4 py-2.5 font-mono text-xs uppercase tracking-wider text-blaze" data-testid="event-detail-status-note">
                  {event.status_note}
                </p>
              )}
              {event.description && (
                <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-300" data-testid="event-detail-description">
                  {event.description}
                </p>
              )}
              <p className="mt-8 max-w-xl text-sm leading-relaxed text-slate-400">
                Zet een alert en ontvang direct een seintje per e-mail of WhatsApp zodra de
                kaartverkoop van {event.name} start. Liever zelf in de gaten houden? Zet de start van
                de verkoop in je agenda.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="h-fit rounded-3xl border border-white/10 bg-panel p-6 sm:p-8"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                Kaartverkoop start
              </p>
              {event.status === "afgelopen" && (
                <p
                  className="mt-3 inline-block rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-slate-400"
                  data-testid="event-detail-archived-note"
                >
                  Dit event heeft plaatsgevonden — houd de site in de gaten voor de volgende editie.
                </p>
              )}
              {isExpected ? (
                <p className="mt-3 font-display text-xl font-bold text-slate-200">
                  Datum wordt bekendgemaakt
                </p>
              ) : (
                <p className="mt-3 font-mono text-lg font-semibold text-volt">
                  {fmtDate(event.sale_start, true)} <span className="text-slate-500 text-sm">(NL-tijd)</span>
                </p>
              )}
              {event.status === "binnenkort" && (
                <div className="mt-5" data-testid="event-detail-countdown">
                  <Countdown target={event.sale_start} large />
                </div>
              )}
              {isLive && (
                <p className="mt-4 flex items-center gap-1.5 text-sm font-medium text-blaze">
                  <Zap className="h-4 w-4" /> Verkoop is gestart — wees er snel bij
                </p>
              )}

              {isLive && event.ticket_url && (
                <a
                  href={event.ticket_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="event-detail-buy-button"
                  className="mt-6 flex items-center justify-center gap-2 rounded-full bg-blaze px-6 py-3.5 text-sm font-bold text-black transition-transform hover:scale-[1.03] hover:bg-blaze-hover"
                >
                  <Ticket className="h-4 w-4" /> {event.button_text || "Koop tickets"}
                  <ExternalLink className="h-3.5 w-3.5 opacity-60" />
                </a>
              )}

              <button
                onClick={() => setRemindOpen(true)}
                data-testid="event-detail-alert-button"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-volt px-6 py-3.5 text-sm font-bold text-black transition-transform hover:scale-[1.03] hover:bg-volt-hover"
              >
                <BellRing className="h-4 w-4" /> Stel alert in
              </button>

              {gUrl && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="event-detail-calendar-button"
                      className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-volt/60 hover:text-volt"
                    >
                      <CalendarPlus className="h-4 w-4" /> Zet in mijn agenda
                      <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="border-white/10 bg-[#12121C] text-slate-200">
                    <DropdownMenuItem asChild>
                      <a
                        data-testid="event-detail-google-calendar-link"
                        href={gUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="cursor-pointer"
                        onClick={() => toast.success("Google Agenda geopend")}
                      >
                        Google Agenda
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="event-detail-apple-calendar-link"
                      className="cursor-pointer"
                      onClick={() => {
                        downloadIcs(event);
                        toast.success("Agenda-bestand (.ics) gedownload");
                      }}
                    >
                      Apple Agenda (.ics)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
      <ReminderModal event={event} open={remindOpen} onClose={() => setRemindOpen(false)} />
    </div>
  );
}
